export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon?: string;
  targetSelector: string | (() => HTMLElement | null);
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  beforeStep?: () => Promise<void> | void;
  afterStep?: () => Promise<void> | void;
  highlightPadding?: number;
  borderRadius?: number;
  customMarker?: boolean;
}

export interface OnboardingTourConfig {
  version?: string;
  minScreenWidth?: number;
  autoStartDelayMs?: number;
  drawer?: any | null;
}

export class OnboardingTour {
  private static STORAGE_KEY = 'skyskout_onboarding_completed';
  private static LEGACY_STORAGE_KEY = 'gw_onboarding_completed';
  private version: string;
  private minScreenWidth: number;
  private autoStartDelayMs: number;
  private drawer: any | null = null;

  private currentStepIndex = 0;
  private isActive = false;
  private steps: OnboardingStep[] = [];

  // DOM elements
  private overlayContainer!: HTMLElement;
  private svgBackdrop!: SVGSVGElement;
  private maskRect!: SVGRectElement;
  private spotlightRing!: HTMLElement;
  private cardContainer!: HTMLElement;
  private mapRippleMarker: HTMLElement | null = null;

  private boundKeyHandler: (e: KeyboardEvent) => void;
  private boundResizeHandler: () => void;

  constructor(config?: OnboardingTourConfig) {
    const appConfig = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.onboarding) || {};
    this.version = config?.version || appConfig.version || 'v1';
    this.minScreenWidth = config?.minScreenWidth ?? (appConfig.min_screen_width ?? 768);
    this.autoStartDelayMs = config?.autoStartDelayMs ?? (appConfig.auto_start_delay_ms ?? 800);
    this.drawer = config?.drawer || null;

    this.boundKeyHandler = this.handleKeyDown.bind(this);
    this.boundResizeHandler = this.handleResize.bind(this);

    this.initSteps();
    this.buildDOM();
  }

  public setDrawer(drawer: any) {
    this.drawer = drawer;
  }

  public hasCompletedTour(): boolean {
    try {
      if (typeof localStorage === 'undefined') return false;
      return localStorage.getItem(OnboardingTour.STORAGE_KEY) === this.version ||
             localStorage.getItem(OnboardingTour.LEGACY_STORAGE_KEY) === this.version;
    } catch {
      return false;
    }
  }

  public markCompleted() {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(OnboardingTour.STORAGE_KEY, this.version);
      }
    } catch (e) {
      console.warn('Could not save onboarding status to localStorage:', e);
    }
  }

  public reset() {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(OnboardingTour.STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Could not reset onboarding status in localStorage:', e);
    }
  }

  private initSteps() {
    this.steps = [
      {
        id: 'drawer_personas',
        title: 'Weather & Activity Layers Drawer',
        description: 'Browse curated activity layers (Photography, Hiking, Stargazing, Winter Sports) or expand 8 professional atmospheric weather categories. Activating layers expands their controls directly in-place.',
        icon: '🗂️',
        targetSelector: () => document.querySelector('#gw-right-layer-drawer') || document.querySelector('.gw-drawer-drag-handle') || document.querySelector('.gw-bottom-drawer'),
        placement: 'left',
        highlightPadding: 8,
        borderRadius: 14,
        beforeStep: async () => {
          if (this.drawer && typeof this.drawer.open === 'function') {
            this.drawer.open();
            await new Promise(r => setTimeout(r, 220));
          } else if (this.drawer && typeof this.drawer.setSnapState === 'function') {
            this.drawer.setSnapState('standard');
            await new Promise(r => setTimeout(r, 220));
          }
        }
      },
      {
        id: 'layers_activation',
        title: 'Base Maps & Map Styles',
        description: 'CLick here to switch between various base map styles including Terrain, Topographical, Satellite and Street Maps.',
        icon: '🥞',
        targetSelector: () => {
          return document.querySelector('.gw-basemap-btn') ||
            document.querySelector('.gw-basemap-switcher-container') ||
            document.querySelector('.gw-bottom-drawer');
        },
        placement: 'right',
        highlightPadding: 6,
        borderRadius: 12,
        beforeStep: async () => {
          if (this.drawer) {
            this.drawer.setSnapState('collapsed');
            await new Promise(r => setTimeout(r, 220));
          }
        }
      },
      {
        id: 'timeline_scrubber',
        title: 'Interactive Forecast Timeline',
        description: 'Drag the date/time slider to see recent observed weather phoenomena like dopler radar, satellite cloud layers, rain accumulation and more.  Click forward in time "NOW" and beyond, to cycled through forecast hours and see into the future.',
        icon: '⏱️',
        targetSelector: '#timeline-container',
        placement: 'top',
        highlightPadding: 8,
        borderRadius: 14,
        beforeStep: async () => {
          if (this.drawer) {
            this.drawer.setSnapState('collapsed');
            await new Promise(r => setTimeout(r, 220));
          }
        }
      },
      {
        id: 'map_inspection',
        title: 'Map Point Forecast & Telemetry',
        description: 'Click anywhere on the map to unlock location information (elevation, lat/lon), get a detailed forecast that spot that shows temperatures through out the elevation ranges in that area, get monthly calendar view of this years fall foliage forecast, and much more.  With the dark skies forecast layer anebled, click any spot to enable a 3D Augmented Reality View of the Milky Way for night sky viewing and photography planning.  Or open the solar and lunar tools to figure out if your next outing aligns with a new moon, a harvest moon or if it is in a dark area based on the Bortle Map.',
        icon: '📍',
        targetSelector: () => document.getElementById('map-container') || document.body,
        placement: 'center',
        customMarker: true,
        beforeStep: async () => {
          if (this.drawer) {
            this.drawer.setSnapState('collapsed');
            await new Promise(r => setTimeout(r, 220));
          }
        }
      },
      {
        id: 'search_nav',
        title: 'Instant Search & Navigation',
        description: 'Search for mountains/peaks, cities, national parks, or enter GPS coordinates to instantly jump to any region. You can replay this interactive tour or view our proprietary layer guides anytime via the "About SkySkout" button in the bottom drawer.',
        icon: '🔍',
        targetSelector: '.gw-search-widget',
        placement: 'bottom',
        highlightPadding: 8,
        borderRadius: 14,
        beforeStep: async () => {
          if (this.drawer) {
            this.drawer.setSnapState('collapsed');
            await new Promise(r => setTimeout(r, 220));
          }
        }
      }
    ];
  }

  private buildDOM() {
    // 1. Overlay container
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.className = 'gw-tour-overlay-container';

    // 2. SVG Backdrop Mask
    const svgNS = 'http://www.w3.org/2000/svg';
    this.svgBackdrop = document.createElementNS(svgNS, 'svg');
    this.svgBackdrop.setAttribute('class', 'gw-tour-svg-backdrop');
    this.svgBackdrop.setAttribute('width', '100%');
    this.svgBackdrop.setAttribute('height', '100%');

    const defs = document.createElementNS(svgNS, 'defs');
    const mask = document.createElementNS(svgNS, 'mask');
    mask.setAttribute('id', 'gw-tour-cutout-mask');

    const maskBackground = document.createElementNS(svgNS, 'rect');
    maskBackground.setAttribute('x', '0');
    maskBackground.setAttribute('y', '0');
    maskBackground.setAttribute('width', '100%');
    maskBackground.setAttribute('height', '100%');
    maskBackground.setAttribute('fill', 'white');

    this.maskRect = document.createElementNS(svgNS, 'rect');
    this.maskRect.setAttribute('class', 'gw-tour-svg-mask-rect');
    this.maskRect.setAttribute('x', '0');
    this.maskRect.setAttribute('y', '0');
    this.maskRect.setAttribute('width', '0');
    this.maskRect.setAttribute('height', '0');
    this.maskRect.setAttribute('rx', '12');
    this.maskRect.setAttribute('fill', 'black');

    mask.appendChild(maskBackground);
    mask.appendChild(this.maskRect);
    defs.appendChild(mask);
    this.svgBackdrop.appendChild(defs);

    const darkOverlay = document.createElementNS(svgNS, 'rect');
    darkOverlay.setAttribute('x', '0');
    darkOverlay.setAttribute('y', '0');
    darkOverlay.setAttribute('width', '100%');
    darkOverlay.setAttribute('height', '100%');
    darkOverlay.setAttribute('fill', 'rgba(10, 15, 26, 0.78)');
    darkOverlay.setAttribute('mask', 'url(#gw-tour-cutout-mask)');

    this.svgBackdrop.appendChild(darkOverlay);
    this.overlayContainer.appendChild(this.svgBackdrop);

    // 3. Spotlight ring
    this.spotlightRing = document.createElement('div');
    this.spotlightRing.className = 'gw-tour-spotlight-ring';
    this.overlayContainer.appendChild(this.spotlightRing);

    // 4. Card Container
    this.cardContainer = document.createElement('div');
    this.cardContainer.className = 'gw-tour-card';
    this.overlayContainer.appendChild(this.cardContainer);

    document.body.appendChild(this.overlayContainer);
  }

  public checkAndStart() {
    const isAppEnabled = (typeof __APP_CONFIG__ === 'undefined' || __APP_CONFIG__?.onboarding?.enabled !== false);
    if (!isAppEnabled) return;

    if (window.innerWidth < this.minScreenWidth) {
      return;
    }

    if (!this.hasCompletedTour()) {
      setTimeout(() => {
        this.start(false);
      }, this.autoStartDelayMs);
    }
  }

  public async start(force: boolean = false) {
    if (this.isActive) return;
    if (!force && this.hasCompletedTour()) return;

    this.isActive = true;
    this.currentStepIndex = 0;
    this.overlayContainer.classList.add('active');

    window.addEventListener('keydown', this.boundKeyHandler);
    window.addEventListener('resize', this.boundResizeHandler);

    await this.renderCurrentStep();
  }

  public end() {
    this.isActive = false;
    this.overlayContainer.classList.remove('active');
    this.removeMapRippleMarker();

    window.removeEventListener('keydown', this.boundKeyHandler);
    window.removeEventListener('resize', this.boundResizeHandler);

    this.markCompleted();
  }

  public next() {
    if (this.currentStepIndex < this.steps.length - 1) {
      this.goToStep(this.currentStepIndex + 1);
    } else {
      this.end();
    }
  }

  public prev() {
    if (this.currentStepIndex > 0) {
      this.goToStep(this.currentStepIndex - 1);
    }
  }

  public async goToStep(index: number) {
    if (index < 0 || index >= this.steps.length) return;
    const prevStep = this.steps[this.currentStepIndex];
    if (prevStep && prevStep.afterStep) {
      await prevStep.afterStep();
    }
    this.currentStepIndex = index;
    await this.renderCurrentStep();
  }

  private async renderCurrentStep() {
    const step = this.steps[this.currentStepIndex];
    if (!step) return;

    if (step.beforeStep) {
      await step.beforeStep();
    }

    this.removeMapRippleMarker();

    let targetEl: HTMLElement | null = null;
    if (typeof step.targetSelector === 'function') {
      targetEl = step.targetSelector();
    } else {
      targetEl = document.querySelector(step.targetSelector);
    }

    const pad = step.highlightPadding ?? 8;
    const r = step.borderRadius ?? 12;

    let targetRect: DOMRect;
    if (step.customMarker && step.id === 'map_inspection') {
      // Create a centered ripple marker on map
      this.createMapRippleMarker();
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight * 0.42;
      targetRect = {
        top: cy - 26,
        bottom: cy + 26,
        left: cx - 26,
        right: cx + 26,
        width: 52,
        height: 52,
        x: cx - 26,
        y: cy - 26,
        toJSON: () => { }
      } as DOMRect;
    } else if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        targetRect = rect;
      } else {
        targetRect = {
          top: window.innerHeight / 2 - 50,
          bottom: window.innerHeight / 2 + 50,
          left: window.innerWidth / 2 - 150,
          right: window.innerWidth / 2 + 150,
          width: 300,
          height: 100,
          x: window.innerWidth / 2 - 150,
          y: window.innerHeight / 2 - 50,
          toJSON: () => { }
        } as DOMRect;
      }
    } else {
      targetRect = {
        top: window.innerHeight / 2 - 50,
        bottom: window.innerHeight / 2 + 50,
        left: window.innerWidth / 2 - 150,
        right: window.innerWidth / 2 + 150,
        width: 300,
        height: 100,
        x: window.innerWidth / 2 - 150,
        y: window.innerHeight / 2 - 50,
        toJSON: () => { }
      } as DOMRect;
    }

    // Update SVG Cutout and Spotlight Ring
    const cutoutX = Math.max(0, targetRect.left - pad);
    const cutoutY = Math.max(0, targetRect.top - pad);
    const cutoutW = targetRect.width + pad * 2;
    const cutoutH = targetRect.height + pad * 2;

    this.maskRect.setAttribute('x', `${cutoutX}`);
    this.maskRect.setAttribute('y', `${cutoutY}`);
    this.maskRect.setAttribute('width', `${cutoutW}`);
    this.maskRect.setAttribute('height', `${cutoutH}`);
    this.maskRect.setAttribute('rx', `${r}`);

    this.spotlightRing.style.left = `${cutoutX}px`;
    this.spotlightRing.style.top = `${cutoutY}px`;
    this.spotlightRing.style.width = `${cutoutW}px`;
    this.spotlightRing.style.height = `${cutoutH}px`;
    this.spotlightRing.style.borderRadius = `${r}px`;
    this.spotlightRing.style.opacity = step.customMarker ? '0.2' : '1';

    // Render Card Content
    this.renderCardContent(step, cutoutX, cutoutY, cutoutW, cutoutH);
  }

  private renderCardContent(
    step: OnboardingStep,
    cutoutX: number,
    cutoutY: number,
    cutoutW: number,
    cutoutH: number
  ) {
    const isFirst = this.currentStepIndex === 0;
    const isLast = this.currentStepIndex === this.steps.length - 1;
    const stepNum = this.currentStepIndex + 1;
    const totalSteps = this.steps.length;

    this.cardContainer.innerHTML = `
      <div class="gw-tour-card-header">
        <span class="gw-tour-badge">Step ${stepNum} of ${totalSteps}</span>
        <button class="gw-tour-close-btn" title="Skip Tour" id="gw-tour-close-x">✕</button>
      </div>
      <div class="gw-tour-card-title">
        ${step.icon ? `<span class="gw-tour-icon">${step.icon}</span>` : ''}
        <span>${step.title}</span>
      </div>
      <div class="gw-tour-card-body">
        ${step.description}
      </div>
      <div class="gw-tour-card-footer">
        <div class="gw-tour-dots">
          ${this.steps.map((_, i) => `<div class="gw-tour-dot ${i === this.currentStepIndex ? 'active' : ''}" data-step="${i}"></div>`).join('')}
        </div>
        <div class="gw-tour-buttons">
          <button class="gw-tour-btn gw-tour-btn-skip" id="gw-tour-btn-skip">Skip</button>
          ${!isFirst ? `<button class="gw-tour-btn gw-tour-btn-secondary" id="gw-tour-btn-back">Back</button>` : ''}
          <button class="gw-tour-btn gw-tour-btn-primary" id="gw-tour-btn-next">
            ${isLast ? 'Close Tour' : 'Next →'}
          </button>
        </div>
      </div>
    `;

    // Bind card buttons
    this.cardContainer.querySelector('#gw-tour-close-x')?.addEventListener('click', () => this.end());
    this.cardContainer.querySelector('#gw-tour-btn-skip')?.addEventListener('click', () => this.end());
    this.cardContainer.querySelector('#gw-tour-btn-back')?.addEventListener('click', () => this.prev());
    this.cardContainer.querySelector('#gw-tour-btn-next')?.addEventListener('click', () => this.next());

    const dots = this.cardContainer.querySelectorAll('.gw-tour-dot');
    dots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        const targetStep = parseInt((e.currentTarget as HTMLElement).dataset.step || '0', 10);
        this.goToStep(targetStep);
      });
    });

    // Position Card Smartly with boundary clamping
    this.positionCard(step.placement || 'top', cutoutX, cutoutY, cutoutW, cutoutH);
  }

  private positionCard(
    preferredPlacement: string,
    cutoutX: number,
    cutoutY: number,
    cutoutW: number,
    cutoutH: number
  ) {
    const cardRect = this.cardContainer.getBoundingClientRect();
    const cardW = cardRect.width || 360;
    const cardH = cardRect.height || 220;
    const margin = 16;
    const gap = 16;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let posX = 0;
    let posY = 0;

    if (preferredPlacement === 'center') {
      posX = (vw - cardW) / 2;
      posY = Math.min(vh - cardH - margin, cutoutY + cutoutH + 30);
    } else if (preferredPlacement === 'top') {
      posX = cutoutX + (cutoutW - cardW) / 2;
      posY = cutoutY - cardH - gap;

      // If overflowing off top, flip below
      if (posY < margin) {
        posY = cutoutY + cutoutH + gap;
      }
    } else if (preferredPlacement === 'bottom') {
      posX = cutoutX + (cutoutW - cardW) / 2;
      posY = cutoutY + cutoutH + gap;

      // If overflowing off bottom, flip above
      if (posY + cardH > vh - margin) {
        posY = cutoutY - cardH - gap;
      }
    } else if (preferredPlacement === 'left') {
      posX = cutoutX - cardW - gap;
      posY = cutoutY + (cutoutH - cardH) / 2;
    } else if (preferredPlacement === 'right') {
      posX = cutoutX + cutoutW + gap;
      posY = cutoutY + (cutoutH - cardH) / 2;
    }

    // Clamp horizontally and vertically
    posX = Math.max(margin, Math.min(vw - cardW - margin, posX));
    posY = Math.max(margin, Math.min(vh - cardH - margin, posY));

    this.cardContainer.style.left = `${posX}px`;
    this.cardContainer.style.top = `${posY}px`;
  }

  private createMapRippleMarker() {
    this.removeMapRippleMarker();
    this.mapRippleMarker = document.createElement('div');
    this.mapRippleMarker.className = 'gw-tour-map-target-marker';
    this.mapRippleMarker.style.top = '42%';
    this.mapRippleMarker.style.left = '50%';
    this.mapRippleMarker.innerHTML = `
      <div class="gw-tour-map-target-wave"></div>
      <div class="gw-tour-map-target-center"></div>
    `;
    this.overlayContainer.appendChild(this.mapRippleMarker);
  }

  private removeMapRippleMarker() {
    if (this.mapRippleMarker && this.mapRippleMarker.parentNode) {
      this.mapRippleMarker.parentNode.removeChild(this.mapRippleMarker);
      this.mapRippleMarker = null;
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (!this.isActive) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.end();
    } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  }

  private handleResize() {
    if (!this.isActive) return;
    this.renderCurrentStep();
  }
}
